/**
 * ReporteAsistenciasModal Component
 *
 * Modal que muestra el reporte completo y REAL de asistencias de un grupo
 * para una fecha específica. Renderiza información obtenida desde el backend:
 * asesor, grupo, materia, campus, ciclo, programa, alumnos y estados de
 * asistencia almacenados en la base de datos.
 *
 * Reutiliza el endpoint compartido con la pantalla de registro:
 *   GET /api/escolar/asistencias/estudiantes/<grupo_id>/?fecha=YYYY-MM-DD&parcial=1
 *
 * Diseño y estilos se conservan tal cual (Image 21 del diseño).
 */

import React, { useState, useEffect, useMemo } from "react";
import styles from "./RegistroAsistencias.module.css";
import { getEstudiantesAsistencias } from "../../api/asistencias";

function ReporteAsistenciasModal({ grupo, fecha, infoAsesor, onCerrar }) {
  const [estudiantes, setEstudiantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Cargar estudiantes reales con sus asistencias para la fecha desde la BD
  useEffect(() => {
    let cancelado = false;
    const cargar = async () => {
      if (!grupo?.id_grupo) {
        setLoading(false);
        setEstudiantes([]);
        return;
      }
      try {
        setLoading(true);
        setError("");
        const data = await getEstudiantesAsistencias(grupo.id_grupo, fecha, 1);
        if (cancelado) return;
        setEstudiantes(Array.isArray(data?.estudiantes) ? data.estudiantes : []);
      } catch (e) {
        console.error("[ReporteAsistencias] Error al cargar reporte:", e);
        if (!cancelado) {
          setError(e?.message || "Error al cargar el reporte de asistencias");
          setEstudiantes([]);
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    cargar();
    return () => {
      cancelado = true;
    };
  }, [grupo, fecha]);

  // Cerrar modal con ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onCerrar();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onCerrar]);

  // Estudiantes ordenados alfabéticamente por nombre
  const estudiantesOrdenados = useMemo(() => {
    return [...estudiantes].sort((a, b) =>
      (a?.nombre || "").localeCompare(b?.nombre || "", "es", { sensitivity: "base" })
    );
  }, [estudiantes]);

  const getEstadoClass = (estado) => {
    const clases = {
      PRESENTE: styles.estadoPresenteBadge,
      AUSENTE: styles.estadoAusenteBadge,
      TARDANZA: styles.estadoTardanzaBadge,
      JUSTIFICADA: styles.estadoJustificadaBadge,
    };
    return clases[estado] || styles.estadoPresenteBadge;
  };

  // Datos reales del grupo / asesor (provenientes del listado de grupos del asesor)
  const nombreAsesor =
    grupo?.asesor_nombre || infoAsesor?.nombre || "Sin asesor asignado";
  const codigoGrupo = grupo?.codigo || "";
  const materia = grupo?.materia || grupo?.nombre || "Sin materia";
  const campus = grupo?.campus || "Sin campus";
  const ciclo = grupo?.ciclo || "Sin ciclo";
  const programa = grupo?.programa || grupo?.programa_academico || "Sin programa";
  const horario = grupo?.horario || "Sin horario";

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "";
    try {
      const date = new Date(fechaStr + "T00:00:00");
      return date.toLocaleDateString("es-MX", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return fechaStr;
    }
  };

  // Resumen real de asistencias para mostrar totales
  const resumen = useMemo(() => {
    const r = { PRESENTE: 0, AUSENTE: 0, TARDANZA: 0, JUSTIFICADA: 0 };
    estudiantes.forEach((est) => {
      const estado = est?.asistencia?.estado || "PRESENTE";
      if (r[estado] !== undefined) r[estado] += 1;
    });
    return r;
  }, [estudiantes]);

  return (
    <div className={styles.modalOverlay} onClick={onCerrar}>
      <div className={styles.modalReporte} onClick={(e) => e.stopPropagation()}>
        {/* Header del Modal */}
        <div className={styles.modalReporteHeader}>
          <h2 className={styles.modalReporteTitulo}>Reporte de Asistencias</h2>
          <button className={styles.modalCerrarButtonX} onClick={onCerrar}>
            ✕
          </button>
        </div>

        {/* Información real del grupo */}
        <div className={styles.modalReporteInfo}>
          <div className={styles.infoColumna}>
            <p><strong>Asesor:</strong> {nombreAsesor}</p>
            <p>
              <strong>Grupo:</strong> {codigoGrupo}
              {codigoGrupo && materia ? " - " : ""}
              {materia}
            </p>
            <p><strong>Campus:</strong> {campus}</p>
          </div>
          <div className={styles.infoColumna}>
            <p><strong>Ciclo escolar:</strong> {ciclo}</p>
            <p><strong>Programa:</strong> {programa}</p>
            <p><strong>Horario:</strong> {horario}</p>
          </div>
          <div className={styles.infoColumna}>
            <p><strong>Fecha:</strong> {formatFecha(fecha)}</p>
            <p><strong>Total alumnos:</strong> {estudiantes.length}</p>
            <p style={{ fontSize: "0.85rem", color: "#555" }}>
              P: {resumen.PRESENTE} · A: {resumen.AUSENTE} · T: {resumen.TARDANZA} · J: {resumen.JUSTIFICADA}
            </p>
          </div>
        </div>

        {/* Tabla de Reporte */}
        <div className={styles.modalReporteTablaContainer}>
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <p>Cargando reporte...</p>
            </div>
          ) : error ? (
            <div style={{ padding: "20px", textAlign: "center", color: "#b00020" }}>
              <p>{error}</p>
            </div>
          ) : estudiantesOrdenados.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <p>No hay estudiantes inscritos en este grupo.</p>
            </div>
          ) : (
            <table className={styles.modalReporteTabla}>
              <thead>
                <tr className={styles.modalReporteTablaHeader}>
                  <th>Estudiante</th>
                  <th>Matrícula</th>
                  <th>Estado de Asistencia</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {estudiantesOrdenados.map((estudiante) => {
                  const estado = estudiante?.asistencia?.estado || "PRESENTE";
                  const observaciones = estudiante?.asistencia?.observaciones || "";
                  return (
                    <tr
                      key={estudiante.id_estudiante_grupo || estudiante.id_estudiante}
                      className={styles.modalReporteTablafila}
                    >
                      <td className={styles.estudianteNombreReporte}>
                        {estudiante.nombre || "Sin nombre"}
                      </td>
                      <td>{estudiante.matricula || "Sin matrícula"}</td>
                      <td>
                        <span className={`${styles.estadoBadge} ${getEstadoClass(estado)}`}>
                          {estado}
                        </span>
                      </td>
                      <td>
                        <input
                          type="text"
                          className={styles.inputReporteObservaciones}
                          value={observaciones}
                          readOnly
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Botones de acción */}
        <div className={styles.modalReporteBotones}>
          <button className={styles.modalCerrarButtonSecundario} onClick={onCerrar}>
            CERRAR
          </button>
          <button
            className={styles.modalImprimirButton}
            onClick={() => window.print()}
            disabled={loading || estudiantesOrdenados.length === 0}
          >
            IMPRIMIR
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReporteAsistenciasModal;
